import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps the automatic free gift in the cart in step with the checkout "Free Gift Promo" extension,
 * so shoppers see the gift in the cart drawer before they reach checkout.
 *
 * The gift line is marked with the hidden property `_free_gift: auto`. The checkout extension and the
 * "Free Gift Promo" discount both rely on that marker, and lines without it are the customer's own
 * purchases, which are never changed here.
 *
 * Promo config and qualification live in sections/free-gift-state.liquid.
 */

const SOURCE = 'free-gift-sync';
const GIFT_PROPERTIES = { _free_gift: 'auto' };

/** @type {Promise<void> | null} */
let running = null;
let rerun = false;

/**
 * @typedef {object} FreeGiftState
 * @property {boolean} active - Whether the promo is running, per the server clock.
 * @property {number} endsAt - Promo end, in Unix seconds.
 * @property {number} giftVariantId - The gift variant.
 * @property {boolean} qualifies - Whether the cart has a paid qualifying line.
 * @property {{ key: string, quantity: number }[]} giftLines - Lines carrying the gift marker.
 */

/** @returns {Promise<FreeGiftState | null>} */
async function getState() {
  const response = await fetch(`${Theme.routes.cart_url}?sections=free-gift-state`);
  if (!response.ok) return null;

  const sections = await response.json();
  const html = sections['free-gift-state'];
  if (!html) return null;

  const json = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-free-gift-state]')?.textContent;
  return json ? JSON.parse(json) : null;
}

/**
 * @param {string} key - The line item key.
 * @param {number} quantity - The new quantity.
 */
async function changeLine(key, quantity) {
  const response = await fetch(
    Theme.routes.cart_change_url,
    fetchConfig('json', { body: JSON.stringify({ id: key, quantity }) })
  );
  if (!response.ok) throw new Error(`Free gift: could not update line ${key}`);
}

/**
 * @param {number} variantId - The gift variant.
 * @returns {Promise<boolean>} Whether the gift was added. Fails quietly when the gift is out of stock.
 */
async function addGift(variantId) {
  const response = await fetch(
    Theme.routes.cart_add_url,
    fetchConfig('json', {
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: GIFT_PROPERTIES }] }),
    })
  );
  return response.ok;
}

async function sync() {
  const state = await getState();
  if (!state) return;

  // Pages can be cached, so check the end time on the client as well
  const active = state.active && Date.now() / 1000 <= state.endsAt;
  const wantGift = active && state.qualifies;
  const [giftLine, ...extraGiftLines] = state.giftLines;
  let changed = false;

  // One gift per order: remove every marked line when the cart no longer qualifies, otherwise any duplicates
  for (const line of wantGift ? extraGiftLines : state.giftLines) {
    await changeLine(line.key, 0);
    changed = true;
  }

  if (wantGift) {
    if (!giftLine) {
      changed = (await addGift(state.giftVariantId)) || changed;
    } else if (giftLine.quantity !== 1) {
      await changeLine(giftLine.key, 1);
      changed = true;
    }
  }

  if (!changed) return;

  const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());

  // Without `sections`, cart components re-render themselves (drawer, cart page, cart bubble)
  document.dispatchEvent(new CartUpdateEvent({}, SOURCE, { source: SOURCE, itemCount: cart.item_count }));
}

function scheduleSync() {
  if (running) {
    rerun = true;
    return;
  }

  running = sync()
    .catch((error) => console.error(error))
    .finally(() => {
      running = null;
      if (rerun) {
        rerun = false;
        scheduleSync();
      }
    });
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = /** @type {CartUpdateEvent} */ (event).detail?.data;
  if (data?.source === SOURCE || data?.didError) return;
  scheduleSync();
});

// Catch carts that changed outside the drawer (cart page form posts, other tabs) and gifts left after the promo ends
const initialCartCount = Number(document.querySelector('script[data-free-gift-sync]')?.getAttribute('data-cart-count'));
if (initialCartCount > 0) scheduleSync();
